'use client';

import React, {
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { getClasses } from '@/_utils/classnames';
import {
  Button,
  Heading,
  Flex,
  Text,
  Icon,
  Dialog,
  Textarea,
  Select,
} from '@/_components';
import styles from './QuestionsLibraryPopup.module.css';
import type {
  QuestionsLibraryPopupProps,
  Category,
  Question,
} from './QuestionsLibraryPopup.types';
import type { SelectItem } from '@/_components/Select/Select.types';

const STORAGE_KEY = 'soraban_questions_library';

// Helper to format relative time
const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(diff / 604800000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) {
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
};

// Load questions from localStorage or return initial questions
const loadQuestions = (): Question[] => {
  if (typeof window === 'undefined') return initialQuestions;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load questions from localStorage:', error);
  }
  return initialQuestions;
};

// Save questions to localStorage
const saveQuestions = (questions: Question[]): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(questions));
  } catch (error) {
    console.error('Failed to save questions to localStorage:', error);
  }
};

const categories: Category[] = [
  { id: 'all', name: 'All Questions', icon: 'chat' },
  { id: 'questionnaire', name: 'Questionnaire Management', icon: 'file' },
  {
    id: 'questionnaire-questions',
    name: 'Questionnaire Questions Search',
    icon: 'search',
  },
  { id: 'entity', name: 'Entity & Client Management', icon: 'user' },
  { id: 'communication', name: 'Action Items', icon: 'list-check' },
  { id: 'deadlines', name: 'IRS & Tax Research', icon: 'search' },
];

const initialQuestions: Question[] = [
  {
    id: 1,
    category: 'questionnaire',
    text: 'Show me all incomplete questionnaires with entity names',
    favorite: false,
  },
  {
    id: 2,
    category: 'questionnaire-questions',
    text: 'What are the open items for Comprehensive Tax Filing for [entity name] in 2024?',
    favorite: false,
  },
  {
    id: 3,
    category: 'questionnaire-questions',
    text: 'What did [entity name] put for marital status in questionnaire in 2024?',
    favorite: false,
  },
  {
    id: 4,
    category: 'questionnaire',
    text: 'List all questionnaires that are overdue',
    favorite: false,
  },
  {
    id: 5,
    category: 'questionnaire',
    text: 'Show me questionnaires pending client review',
    favorite: false,
  },
  {
    id: 6,
    category: 'communication',
    text: 'Send a reminder to [entity name] about their pending questionnaire',
    favorite: false,
  },
  {
    id: 7,
    category: 'communication',
    text: 'What is the last message from [entity name]?',
    favorite: false,
  },
  {
    id: 8,
    category: 'deadlines',
    text: 'Can you look at IRS Tax Relief, cross-reference the 2025 disaster relief areas?',
    favorite: false,
  },
  {
    id: 9,
    category: 'entity',
    text: 'Find <entity name>',
    favorite: false,
  },
  {
    id: 10,
    category: 'entity',
    text: 'What is the address for <entity name>?',
    favorite: false,
  },
  {
    id: 11,
    category: 'entity',
    text: 'Show me all entities in CA state',
    favorite: false,
  },
  {
    id: 12,
    category: 'entity',
    text: 'Which clients answered no to had tax return last year?',
    favorite: false,
  },
  {
    id: 13,
    category: 'entity',
    text: 'Who is the assigned firm user for <entity name>?',
    favorite: false,
  },
  {
    id: 14,
    category: 'entity',
    text: 'Which companies started a new business?',
    favorite: false,
  },
  {
    id: 15,
    category: 'questionnaire',
    text: 'Open Comprehensive Tax Filing questionnaire for <entity name>',
    favorite: false,
  },
  {
    id: 16,
    category: 'questionnaire',
    text: 'What is the status for Simple Tax Filing for <entity name>?',
    favorite: false,
  },
  {
    id: 17,
    category: 'questionnaire-questions',
    text: 'What did <entity name> answer for his marital status for 2024 comprehensive tax filing questionnaire',
    favorite: false,
  },
  {
    id: 18,
    category: 'questionnaire-questions',
    text: 'What did <entity name> put for "Do you have a spouse?" in Service selection questionnaire',
    favorite: false,
  },
  {
    id: 19,
    category: 'questionnaire',
    text: 'Which questionnaires are overdue?',
    favorite: false,
  },
  {
    id: 20,
    category: 'questionnaire',
    text: 'Show all questionnaires currently in progress',
    favorite: false,
  },
  {
    id: 21,
    category: 'questionnaire',
    text: 'Which questionnaires have pending documents still needed?',
    favorite: false,
  },
  {
    id: 22,
    category: 'questionnaire-questions',
    text: 'For <entity name> 2024, how many items are left and how many are answered?',
    favorite: false,
  },
  {
    id: 23,
    category: 'questionnaire-questions',
    text: 'Did we get the engagement letter signed for <entity name> in the questionnaire?',
    favorite: false,
  },
  {
    id: 24,
    category: 'questionnaire-questions',
    text: 'For <entity name>, list Q&A where a prior-year value exists and compare to current answer.',
    favorite: false,
  },
  {
    id: 25,
    category: 'questionnaire-questions',
    text: 'What are the open items for <entity name>?',
    favorite: false,
  },
  {
    id: 26,
    category: 'questionnaire',
    text: 'Which questionnaires require signatures from all owners?',
    favorite: false,
  },
  {
    id: 27,
    category: 'questionnaire',
    text: 'Which questionnaires are locked?',
    favorite: false,
  },
];

export const QuestionsLibraryPopup: React.FC<QuestionsLibraryPopupProps> = ({
  isOpen,
  onClose,
  onQuestionSelect,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [questions, setQuestions] = useState<Question[]>(loadQuestions);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(
    null,
  );
  const [editingText, setEditingText] = useState('');
  const [deletingQuestionId, setDeletingQuestionId] = useState<number | null>(
    null,
  );
  const [newQuestionText, setNewQuestionText] = useState('');
  const [newQuestionCategory, setNewQuestionCategory] =
    useState('questionnaire');
  const [draggedQuestion, setDraggedQuestion] = useState<number | null>(null);
  const editingInputRef = useRef<HTMLInputElement>(null);

  const popupClasses: string = getClasses(styles.sidePopup, {
    [styles.sidePopupOpen]: isOpen,
  });

  const overlayClasses = getClasses(styles.popupOverlay, {
    [styles.active]: isOpen,
  });

  const filteredQuestions = useMemo(() => {
    return questions.filter((q) => {
      const matchesSearch = q.text
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesCategory =
        activeCategory === 'all' || q.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [questions, searchQuery, activeCategory]);

  const recentlyUsed = useMemo(() => {
    return [...questions]
      .filter((q) => q.lastUsed !== undefined)
      .sort((a, b) => (b.lastUsed || 0) - (a.lastUsed || 0))
      .slice(0, 3);
  }, [questions]);

  // Save questions to localStorage whenever they change
  useEffect(() => {
    saveQuestions(questions);
  }, [questions]);

  const handleQuestionClick = useCallback(
    (questionText: string, questionId: number) => {
      // Update lastUsed timestamp
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === questionId ? { ...q, lastUsed: Date.now() } : q,
        ),
      );
      onQuestionSelect(questionText);
      onClose?.();
    },
    [onQuestionSelect, onClose],
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose?.();
      }
    },
    [onClose],
  );

  const handleEdit = useCallback(
    (questionId: number, e: React.MouseEvent) => {
      e.stopPropagation();
      const question = questions.find((q) => q.id === questionId);
      if (question) {
        setEditingQuestionId(questionId);
        setEditingText(question.text);
      }
    },
    [questions],
  );

  const handleEditSave = useCallback(() => {
    if (!editingText.trim() || editingQuestionId === null) {
      setEditingQuestionId(null);
      return;
    }

    setQuestions((prev) =>
      prev.map((q) =>
        q.id === editingQuestionId ? { ...q, text: editingText.trim() } : q,
      ),
    );
    setEditingQuestionId(null);
    setEditingText('');
  }, [editingText, editingQuestionId]);

  const handleEditCancel = useCallback(() => {
    setEditingQuestionId(null);
    setEditingText('');
  }, []);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleEditSave();
      } else if (e.key === 'Escape') {
        handleEditCancel();
      }
    },
    [handleEditSave, handleEditCancel],
  );

  const handleDelete = useCallback(
    (questionId: number, e: React.MouseEvent) => {
      e.stopPropagation();
      setDeletingQuestionId(questionId);
      setIsDeleteDialogOpen(true);
    },
    [],
  );

  const handleAddQuestion = useCallback(() => {
    setNewQuestionText('');
    setNewQuestionCategory('questionnaire');
    setIsAddDialogOpen(true);
  }, []);

  const handleSaveNewQuestion = useCallback(() => {
    if (!newQuestionText.trim()) return;

    const newQuestion: Question = {
      id: Math.max(...questions.map((q) => q.id)) + 1,
      category: newQuestionCategory,
      text: newQuestionText.trim(),
      favorite: false,
    };
    setQuestions((prev) => [newQuestion, ...prev]);

    setIsAddDialogOpen(false);
    setNewQuestionText('');
  }, [newQuestionText, newQuestionCategory, questions]);

  const handleConfirmDelete = useCallback(() => {
    if (deletingQuestionId !== null) {
      setQuestions((prev) => prev.filter((q) => q.id !== deletingQuestionId));
      setIsDeleteDialogOpen(false);
      setDeletingQuestionId(null);
    }
  }, [deletingQuestionId]);

  const handleDragStart = useCallback((questionId: number) => {
    setDraggedQuestion(questionId);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, targetQuestionId: number) => {
      e.preventDefault();
      if (draggedQuestion === null || draggedQuestion === targetQuestionId) {
        return;
      }

      setQuestions((prev) => {
        const newQuestions = [...prev];
        const draggedIdx = newQuestions.findIndex(
          (q) => q.id === draggedQuestion,
        );
        const targetIdx = newQuestions.findIndex(
          (q) => q.id === targetQuestionId,
        );

        if (draggedIdx !== -1 && targetIdx !== -1) {
          const [draggedItem] = newQuestions.splice(draggedIdx, 1);
          newQuestions.splice(targetIdx, 0, draggedItem);
        }

        return newQuestions;
      });
    },
    [draggedQuestion],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedQuestion(null);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingQuestionId !== null && editingInputRef.current) {
      editingInputRef.current.focus();
      editingInputRef.current.select();
    }
  }, [editingQuestionId]);

  const QuestionCard = ({
    question,
    isDraggable = false,
  }: {
    question: Question;
    isDraggable?: boolean;
  }) => (
    <div
      className={styles.questionCard}
      onClick={() => handleQuestionClick(question.text, question.id)}
      draggable={isDraggable}
      onDragStart={() => isDraggable && handleDragStart(question.id)}
      onDragOver={(e) => isDraggable && handleDragOver(e, question.id)}
      onDragEnd={handleDragEnd}
    >
      {isDraggable && (
        <button
          className={styles.dragHandle}
          aria-label="Drag to reorder"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Icon name="dots-six-vertical" size={16} />
        </button>
      )}

      <div className={styles.questionContent}>
        <div className={styles.questionHeader}>
          {editingQuestionId === question.id ? (
            <input
              ref={editingInputRef}
              type="text"
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              onKeyDown={handleEditKeyDown}
              onBlur={handleEditSave}
              className={styles.questionEditInput}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <p className={styles.questionText}>{question.text}</p>
          )}
        </div>

        {question.lastUsed && (
          <div className={styles.questionMeta}>
            <span>Last used {formatRelativeTime(question.lastUsed)}</span>
          </div>
        )}
      </div>

      <div className={styles.questionActions}>
        <button
          className={styles.actionButton}
          onClick={(e) => handleEdit(question.id, e)}
          aria-label="Edit question"
        >
          <Icon name="pencil" size={14} />
        </button>
        <button
          className={getClasses(styles.actionButton, styles.delete)}
          onClick={(e) => handleDelete(question.id, e)}
          aria-label="Delete question"
        >
          <Icon name="trash" size={14} />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Background Overlay */}
      <div
        className={overlayClasses}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Side Popup Panel */}
      <div
        ref={popupRef}
        className={popupClasses}
        role="dialog"
        aria-modal="true"
        aria-label="Questions Library"
      >
        {/* Popup Header */}
        <div className={styles.headerContainer}>
          <Flex isBetween={true} paddingY={20} paddingX={16}>
            <Flex isColumn={true} gap={2}>
              <Heading level={4}>Questions Library</Heading>
              <Text type="small" color="secondary">
                Manage and organize your question templates
              </Text>
            </Flex>
            <Flex isYCentered gap={8}>
              <Button
                intent="primary"
                iconLeft="plus"
                value="Add Question"
                size="small"
                onClick={handleAddQuestion}
              />
              <Button
                intent="ghost"
                iconLeft="x"
                isIconOnly
                onClick={onClose}
                aria-label="Close questions library"
                value="Close"
              />
            </Flex>
          </Flex>

          {/* Search */}
          <Flex paddingX={16} paddingY={12}>
            <div className={styles.searchContainer}>
              <div className={styles.searchIcon}>
                <Icon name="search" size={16} />
              </div>
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          </Flex>
        </div>

        {/* Categories */}
        <Flex paddingX={16} paddingY={12}>
          <div className={styles.categoriesContainer}>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={getClasses(styles.categoryButton, {
                  [styles.active]: activeCategory === cat.id,
                })}
              >
                <Icon name={cat.icon} size={16} />
                <span>{cat.name}</span>
              </button>
            ))}
          </div>
        </Flex>

        {/* Content */}
        <div className={styles.contentContainer}>
          {/* Recently Used Section */}
          {activeCategory === 'all' && !searchQuery && (
            <div style={{ marginBottom: '24px' }}>
              <Flex isYCentered gap={8} style={{ marginBottom: '12px' }}>
                <Icon name="clock" size={16} color="accent" />
                <Heading level={5} isSameLine>
                  Recently Used
                </Heading>
              </Flex>
              <div className={styles.questionsGrid}>
                {recentlyUsed.map((q) => (
                  <QuestionCard key={q.id} question={q} />
                ))}
              </div>
            </div>
          )}

          {/* All Questions */}
          <div>
            <Flex isBetween isYCentered style={{ marginBottom: '12px' }}>
              <Heading level={5} isSameLine>
                {activeCategory === 'all'
                  ? 'All Questions'
                  : categories.find((c) => c.id === activeCategory)?.name}
              </Heading>
              <Text type="small" color="secondary">
                {filteredQuestions.length} questions
              </Text>
            </Flex>

            {filteredQuestions.length > 0 ? (
              <div className={styles.questionsGrid}>
                {filteredQuestions.map((q) => (
                  <QuestionCard key={q.id} question={q} isDraggable={true} />
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>
                  <Icon name="chat" size={32} color="secondary" />
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <Text color="secondary">No questions found</Text>
                </div>
                <Text type="small" color="secondary">
                  Try adjusting your search or filters
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Question Dialog */}
      <Dialog
        isOpen={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
        }}
        title="Add New Question"
      >
        <Flex isColumn gap={20} paddingY={20} paddingX={4}>
          <Flex gap={12}>
            <div className={styles.addQuestionIcon}>
              <Icon name="plus-circle" size={24} />
            </div>
            <Flex isColumn gap={4} style={{ flex: 1 }}>
              <Text fontWeight="medium">Create a new question template</Text>
              <Text type="small" color="secondary">
                Save frequently asked questions for quick access
              </Text>
            </Flex>
          </Flex>

          <div className={styles.formContainer}>
            <Textarea
              label="Question Text"
              value={newQuestionText}
              onChange={(e) => setNewQuestionText(e.target.value)}
              placeholder="e.g., What are the open items for [entity name] in 2024?"
              rows={4}
            />
          </div>

          <div className={styles.formContainer}>
            <Select
              label="Category"
              items={categories
                .filter((cat) => cat.id !== 'all')
                .map(
                  (cat): SelectItem => ({
                    value: cat.id,
                    name: cat.name,
                  }),
                )}
              selectedValue={newQuestionCategory}
              onChange={(e) => setNewQuestionCategory(e.target.value)}
            />
          </div>

          <Flex gap={8} style={{ marginTop: '8px' }}>
            <Button
              intent="ghost"
              value="Cancel"
              onClick={() => {
                setIsAddDialogOpen(false);
              }}
              className={styles.cancelButton}
            />
            <Button
              intent="primary"
              value="Add Question"
              onClick={handleSaveNewQuestion}
              isDisabled={!newQuestionText.trim()}
              className={styles.addButton}
            />
          </Flex>
        </Flex>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setDeletingQuestionId(null);
        }}
        title="Delete Question"
        isNarrow
      >
        <Flex isColumn gap={20} paddingY={20} paddingX={4}>
          <Flex gap={12} isYCentered>
            <div className={styles.deleteWarningIcon}>
              <Icon name="warning" size={24} />
            </div>
            <Flex isColumn gap={4}>
              <Text fontWeight="medium">
                Are you sure you want to delete this question?
              </Text>
              <Text type="small" color="secondary">
                This action cannot be undone.
              </Text>
            </Flex>
          </Flex>

          <Flex gap={8} style={{ marginTop: '4px' }}>
            <Button
              intent="ghost"
              value="Cancel"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeletingQuestionId(null);
              }}
              className={styles.cancelButton}
            />
            <Button
              intent="secondary"
              value="Delete"
              onClick={handleConfirmDelete}
              className={styles.deleteButton}
            />
          </Flex>
        </Flex>
      </Dialog>
    </>
  );
};
